import type { PropsWithChildren } from 'react';
import styled, { css } from 'styled-components';
import { connectInstanceWithProps } from 'streams/utils/connectInstanceWithProps';
import { ArdoqLoaderComponent, LoaderColor, LoaderSize } from '@ardoq/loader';
import { t, type I18nLocale } from '@ardoq/i18n';
import {
  getLoadingIndicatorViewModel$,
  type LoadingIndicatorSource,
  type LoadingIndicatorViewModel,
} from './withLoadingIndicatorViewModel$';

const Container = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  flex: 1;
  min-height: 0;

  > .content {
    height: 100%;
    width: 100%;
  }
`;

const LoaderOverlay = styled.div<{ $isFromPresentation: boolean }>`
  width: 100%;
  height: 100%;
  z-index: 100;
  pointer-events: none;
  display: flex;
  justify-content: center;
  align-items: center;
  ${props =>
    props.$isFromPresentation
      ? css`
          position: fixed;
          top: 0;
          left: 0;
          background: rgba(255, 255, 255, 0.8);
        `
      : css`
          position: absolute;
          background: rgba(255, 255, 255, 0.5);
        `}
`;

type LoadingIndicatorProps = PropsWithChildren<
  LoadingIndicatorViewModel & { i18nLocale: I18nLocale }
>;

const LoadingIndicator = ({
  isLoaderShown,
  isContentHidden,
  isAriaBusy,
  isFromPresentation,
  transitionDuration,
  i18nLocale,
  children,
}: LoadingIndicatorProps) => (
  <Container>
    <LoaderOverlay
      aria-label={t(
        i18nLocale,
        'tabview:loading.viewAriaLabel',
        'Loading view'
      )}
      aria-busy={isAriaBusy}
      role="status"
      $isFromPresentation={isFromPresentation}
      style={{ opacity: isLoaderShown ? 1 : 0, transitionDuration }}
    >
      <ArdoqLoaderComponent
        size={LoaderSize.LARGE}
        loaderColor={LoaderColor.BRAND}
        loaderText={t(
          i18nLocale,
          'tabview:loading.dataText',
          'Loading data...'
        )}
      />
    </LoaderOverlay>
    <div
      className="content"
      style={{ visibility: isContentHidden ? 'hidden' : undefined }}
    >
      {children}
    </div>
  </Container>
);

type LoadingIndicatorInstanceProps = {
  viewInstanceId: string;
  loadingSource: LoadingIndicatorSource;
  showContentWhileLoading: boolean;
};

const WithLoadingIndicator = connectInstanceWithProps(
  LoadingIndicator,
  ({
    viewInstanceId,
    loadingSource,
    showContentWhileLoading,
  }: LoadingIndicatorInstanceProps) =>
    getLoadingIndicatorViewModel$({
      viewInstanceId,
      loadingSource,
      showContentWhileLoading,
    })
);

export { WithLoadingIndicator };
